import { useEffect, useState } from "react";
import * as Sentry from "@sentry/react";
import { saveAs } from "file-saver";
import {
  Box,
  Typography,
  CircularProgress,
  Paper,
  Link,
  Button,
  TextField,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import DataObjectIcon from "@mui/icons-material/DataObject";
import { startCase } from "lodash";
import { csvFormat } from "d3-dsv";

import { useTrueBizApi } from "../api";
import { APIRiskDefinition } from "../types/APIRiskDefinition";

export default function RiskDefinitions() {
  const api = useTrueBizApi();
  const [risks, setRisks] = useState<APIRiskDefinition[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [downloading, setDownloading] = useState<boolean>(false);
  const [search, setSearch] = useState<string>("");

  useEffect(() => {
    const getDataExports = async () => {
      try {
        const records = await api.getRiskDefinitions();
        setRisks(records);
      } catch (e) {
        Sentry.captureException(e);
      } finally {
        setLoading(false);
      }
    };
    getDataExports();
  }, [api]);

  const onDownloadCSV = async () => {
    try {
      setDownloading(true);
      const csvContent = csvFormat(risks);
      const csv = new Blob([csvContent], {
        type: "text/csv",
      });
      saveAs(csv, "truebiz_risk_definitions.csv");
    } catch (e) {
      Sentry.captureException(e);
    } finally {
      setDownloading(false);
    }
  };

  const filteredRisks = risks.filter((risk: APIRiskDefinition) => {
    for (const value of Object.values(risk)) {
      if (value.toLowerCase().includes(search.toLowerCase())) {
        return true;
      }
    }
    return false;
  });

  const gather: any = {};
  filteredRisks.forEach((risk: APIRiskDefinition) => {
    if (!(risk.category in gather)) {
      gather[risk.category] = {};
    }

    if (!(risk.subcategory in gather[risk.category])) {
      gather[risk.category][risk.subcategory] = [];
    }

    gather[risk.category][risk.subcategory].push(risk);
  });

  return (
    <Box sx={{ pb: 3 }}>
      <Box display="flex" justifyContent="space-between">
        <Typography variant="h4" component="h1" fontWeight={700} gutterBottom>
          Risk Definitions
          {loading && <CircularProgress size={28} sx={{ marginLeft: 1 }} />}
        </Typography>
        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 3 }}>
          <TextField
            id="outlined-basic"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            label="Search"
            variant="outlined"
            size="small"
          />
          <Link
            href="https://ae.truebiz.io/api/v1/docs#tag/Web-Presence-Review/operation/core_api_company_review_company"
            target="_blank"
          >
            <Button sx={{ mb: 2 }}>
              <DataObjectIcon sx={{ ml: -0.5, mr: 1 }} /> View API Documentation
              & Data Dictionary
            </Button>
          </Link>
          <Button
            sx={{ mb: 2 }}
            onClick={onDownloadCSV}
            disabled={downloading || loading}
          >
            <DownloadIcon sx={{ ml: -0.5, mr: 1 }} /> Download CSV
          </Button>
        </Box>
      </Box>
      <Paper elevation={2} sx={{ p: 4 }}>
        {Object.keys(gather).map((category: string) => {
          return Object.keys(gather[category]).map((subcategory: string) => (
            <Box pb={2} key={subcategory}>
              <Typography variant="h5" fontWeight="bold">
                {startCase(category)} -{" "}
                {startCase(subcategory).replace("_", " ")}
              </Typography>
              {gather[category][subcategory].map((risk: APIRiskDefinition) => (
                <Box key={risk.name}>
                  <Typography variant="subtitle1">
                    <strong>{risk.display_name}</strong>
                  </Typography>
                  <Typography variant="body1" sx={{ ml: 2 }}>
                    {risk.description}
                  </Typography>
                </Box>
              ))}
            </Box>
          ));
        })}
      </Paper>
    </Box>
  );
}
